import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import Routes from './routes'
import { AuthProvider } from './contexts/AuthContext'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as SonnerToaster } from '@/components/ui/sonner'
import { supabase } from '@/integrations/supabase/client'
import { initAuthLeader } from '@/integrations/supabase/authLeader'
import { ErrorBoundary } from '@/components/ErrorBoundary'

// Recover from stale-deploy chunk load failures (old index.html referencing
// hashed bundles that no longer exist on the server). One-shot reload guarded
// by sessionStorage so a truly broken deploy doesn't loop.
const CHUNK_RELOAD_KEY = 'chunk-reload-attempted';
const isChunkLoadError = (msg: unknown) => {
  const s = typeof msg === 'string' ? msg : (msg as { message?: string } | null)?.message ?? '';
  return /Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(s);
};
const handleChunkError = (source: unknown) => {
  if (!isChunkLoadError(source)) return;
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  window.location.reload();
};
window.addEventListener('error', (e) => handleChunkError(e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => handleChunkError(e.reason));
// Clear the guard on a successful full load so the next stale-deploy can self-heal.
window.addEventListener('load', () => sessionStorage.removeItem(CHUNK_RELOAD_KEY));

// Elect a single tab per browser to drive auth token refresh.
// Prevents per-IP 429 storms on /auth/v1/token.
if (!window.location.pathname.startsWith('/benchmark-webapp')) {
  initAuthLeader(supabase);
}

// Create a client with improved data fetching behavior
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 10000,
      gcTime: 300000,
    },
  },
})

// Public routes that must render without waiting on the Supabase auth
// bootstrap (Telegram in-app browsers often have restricted storage, which
// made the auth init hang up to 10s and show a blank/spinner screen).
const AUTH_FREE_PATHS = ['/benchmark-webapp'];
const isAuthFree = AUTH_FREE_PATHS.some((p) => window.location.pathname.startsWith(p));

const tree = (
  <>
    <Routes />
    <Toaster />
    <SonnerToaster />
  </>
);

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      {isAuthFree ? tree : <AuthProvider>{tree}</AuthProvider>}
    </QueryClientProvider>
  </ErrorBoundary>
);

